import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Heading,
  HStack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react";
import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { PageContent } from "../../components/PageContent/PageContent";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { ViewControl } from "../../components/ViewControl/ViewControl";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import { useToast } from "../../hooks/useToast";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { GeoLocationsTable } from "../../components/GeoFenceConfiguration/GeoLocationsTable";
import { GeoLocationGroupsTable } from "../../components/GeoFenceConfiguration/GeoLocationGroupsTable";
import { PlusIcon } from "../../styles/icons";

type ResultState =
  | {
      kind: "migrate";
      summary: CherryHubAdminApi.LocationGroupMigrationSummary;
    }
  | {
      kind: "unMigrate";
      summary: CherryHubAdminApi.LocationGroupUnMigrationSummary;
    }
  | null;

export const GeoFenceConfigurationDetail: React.FC = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const adminApiClient = useAdminApiClient();
  const { showSuccessToast, showErrorToast } = useToast();
  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();

  const [migrating, setMigrating] = useState(false);
  const [unMigrating, setUnMigrating] = useState(false);
  const [result, setResult] = useState<ResultState>(null);

  const {
    data: geoLocations,
    isLoading: geoLocationsLoading,
    refresh: refreshGeoLocations,
  } = useAdminApiRequest(
    (apiClient) => (businessId ? apiClient.getGeoLocations(businessId) : null),
    [businessId]
  );

  const [deletingGeoLocation, setDeletingGeoLocation] = useState(false);

  const {
    data: geoLocationGroups,
    isLoading: geoLocationGroupsLoading,
    refresh: refreshGeoLocationGroups,
  } = useAdminApiRequest(
    (apiClient) =>
      businessId ? apiClient.getGeoLocationGroups(businessId) : null,
    [businessId]
  );

  const [deletingGeoLocationGroup, setDeletingGeoLocationGroup] =
    useState(false);

  const handleMigrate = async () => {
    if (!businessId) return;

    const { ok: confirmed } = await showConfirmationDialog(
      "Migrate to new Geo-Fence configuration?",
      "This will backfill GeoLocationGroupId on every PassConfiguration owned by this business. The operation is idempotent.",
      "Migrate"
    );
    if (!confirmed) return;

    setMigrating(true);
    try {
      const response = await adminApiClient.migrateLocationGroups(businessId);
      if (response.ok) {
        setResult({ kind: "migrate", summary: response.data });
        showSuccessToast("Migration completed.");
      } else {
        showErrorToast(response.message ?? "Migration failed.");
      }
    } finally {
      setMigrating(false);
    }
  };

  const handleDeleteGeoLocation = async (
    geoLocation: CherryHubAdminApi.GeoLocation
  ) => {
    if (!businessId || !geoLocation.Meta?.Id) return;

    const { ok: confirmed } = await showConfirmationDialog(
      "Delete Geo Location?",
      `Are you sure you wish to delete this geo location: ${
        geoLocation.DisplayName ?? geoLocation.GeoLocationId
      }?`,
      "Delete"
    );
    if (!confirmed) return;

    setDeletingGeoLocation(true);
    try {
      const response = await adminApiClient.deleteGeoLocation(
        businessId,
        geoLocation.Meta.Id
      );
      if (response.ok) {
        showSuccessToast("Geo Location deleted.");
        refreshGeoLocations();
      } else {
        showErrorToast(response.message ?? "Failed to delete Geo Location.");
      }
    } finally {
      setDeletingGeoLocation(false);
    }
  };

  const handleDeleteGeoLocationGroup = async (
    geoLocationGroup: CherryHubAdminApi.GeoLocationGroup
  ) => {
    if (!businessId || !geoLocationGroup.Meta?.Id) return;

    const { ok: confirmed } = await showConfirmationDialog(
      "Delete Geo Location Group?",
      `Are you sure you wish to delete this geo location group: ${
        geoLocationGroup.DisplayName ?? geoLocationGroup.GeoLocationGroupId
      }?`,
      "Delete"
    );
    if (!confirmed) return;

    setDeletingGeoLocationGroup(true);
    try {
      const response = await adminApiClient.deleteGeoLocationGroup(
        businessId,
        geoLocationGroup.Meta.Id
      );
      if (response.ok) {
        showSuccessToast("Geo Location Group deleted.");
        refreshGeoLocationGroups();
      } else {
        showErrorToast(
          response.message ?? "Failed to delete Geo Location Group."
        );
      }
    } finally {
      setDeletingGeoLocationGroup(false);
    }
  };

  const handleUnMigrate = async () => {
    if (!businessId) return;

    const { ok: confirmed } = await showConfirmationDialog(
      "Un-Migrate from new Geo-Fence configuration?",
      "This will clear GeoLocationGroupId on every PassConfiguration (published + draft) owned by this business. The operation is idempotent.",
      "Un-Migrate"
    );
    if (!confirmed) return;

    setUnMigrating(true);
    try {
      const response = await adminApiClient.unMigrateLocationGroups(businessId);
      if (response.ok) {
        setResult({ kind: "unMigrate", summary: response.data });
        showSuccessToast("Un-migration completed.");
      } else {
        showErrorToast(response.message ?? "Un-migration failed.");
      }
    } finally {
      setUnMigrating(false);
    }
  };

  return (
    <>
      <PageHeading>
        <PageHeading.Title>Geo-Fence Configuration</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%" spacing={6}>
          <BusinessBreadcrumbs variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${businessId}/geo-fence-configuration`}
              >
                Geo-Fence Configuration
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>

          <HStack spacing={4}>
            <Button
              colorScheme="cherryButton"
              onClick={handleMigrate}
              isLoading={migrating}
              loadingText="Migrating"
              isDisabled={unMigrating}
            >
              Migrate to new Geo-Fence configuration
            </Button>
            <Button
              colorScheme="cherryButton"
              variant="outline"
              onClick={handleUnMigrate}
              isLoading={unMigrating}
              loadingText="Un-Migrating"
              isDisabled={migrating}
            >
              Un-Migrate from new Geo-Fence configuration
            </Button>
          </HStack>

          {result && (
            <Box w="100%" pt={2}>
              <Heading size="sm" mb={3}>
                {result.kind === "migrate"
                  ? "Migration Summary"
                  : "Un-Migration Summary"}
              </Heading>
              <VStack alignItems="flex-start" spacing={2} w="100%">
                {Object.entries(result.summary).map(([key, value]) => (
                  <ViewControl
                    key={key}
                    label={key}
                    value={value as string | number}
                  />
                ))}
              </VStack>
            </Box>
          )}

          <Tabs variant="edit" w="100%" isLazy>
            <TabList>
              <HStack spacing={3}>
                <Tab>Geo Locations</Tab>
                <Tab>Geo Location Groups</Tab>
              </HStack>
            </TabList>

            <TabPanels>
              <TabPanel>
                <HStack w="100%" pb={4}>
                  <Box marginLeft="auto">
                    <Button
                      colorScheme="cherryButton"
                      leftIcon={<PlusIcon />}
                      onClick={() =>
                        navigate(
                          `/admin/businesses/${businessId}/geo-fence-configuration/geo-locations/new`
                        )
                      }
                    >
                      New Geo Location
                    </Button>
                  </Box>
                </HStack>
                <GeoLocationsTable
                  geoLocations={geoLocations}
                  isLoading={geoLocationsLoading}
                  disableActions={deletingGeoLocation}
                  onClickGeoLocation={(geoLocation) =>
                    navigate(
                      `/admin/businesses/${businessId}/geo-fence-configuration/geo-locations/${geoLocation.Meta?.Id}`
                    )
                  }
                  onClickDeleteGeoLocation={handleDeleteGeoLocation}
                />
              </TabPanel>

              <TabPanel>
                <HStack w="100%" pb={4}>
                  <Box marginLeft="auto">
                    <Button
                      colorScheme="cherryButton"
                      leftIcon={<PlusIcon />}
                      onClick={() =>
                        navigate(
                          `/admin/businesses/${businessId}/geo-fence-configuration/geo-location-groups/new`
                        )
                      }
                    >
                      New Geo Location Group
                    </Button>
                  </Box>
                </HStack>
                <GeoLocationGroupsTable
                  geoLocationGroups={geoLocationGroups}
                  isLoading={geoLocationGroupsLoading}
                  disableActions={deletingGeoLocationGroup}
                  onClickGeoLocationGroup={(geoLocationGroup) =>
                    navigate(
                      `/admin/businesses/${businessId}/geo-fence-configuration/geo-location-groups/${geoLocationGroup.Meta?.Id}`
                    )
                  }
                  onClickDeleteGeoLocationGroup={handleDeleteGeoLocationGroup}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </PageContent>
      {confirmationElement}
    </>
  );
};
